'use client';

import { useEffect, useState } from 'react';
import { BillingPanel } from '../../../src/components/billing/BillingPanel';

// Phase C-5 Day 3 PM — `/settings/billing` page wrapper. Mirrors the conventions
// from `app/admin/page.tsx`: client-only, static-export friendly, no
// `useSearchParams`, hard-redirects unauthenticated users to `/login`.
//
// The panel itself fetches `/api/billing/me` on mount; this wrapper only
// gates on session presence (via `/api/users/me`) so the deep-link experience
// for an unauthenticated user is "send me to the login page" rather than
// "render an error toast then bounce."

type GateState =
  | { status: 'loading' }
  | { status: 'redirecting' }
  | { status: 'ready'; email: string };

interface MeResponse {
  email?: unknown;
}

export default function BillingPage() {
  const [state, setState] = useState<GateState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetch('/api/users/me', {
          credentials: 'same-origin',
        });
        if (cancelled) return;
        if (!resp.ok) {
          if (resp.status === 401) {
            window.location.href = '/login';
            setState({ status: 'redirecting' });
            return;
          }
          window.location.href = '/';
          setState({ status: 'redirecting' });
          return;
        }
        const body = (await resp.json()) as MeResponse;
        const email = typeof body.email === 'string' ? body.email : null;
        if (!email) {
          window.location.href = '/login';
          setState({ status: 'redirecting' });
          return;
        }
        setState({ status: 'ready', email });
      } catch {
        if (cancelled) return;
        window.location.href = '/';
        setState({ status: 'redirecting' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status !== 'ready') {
    return <main style={{ padding: 24 }}>Loading…</main>;
  }

  return <BillingPanel email={state.email} />;
}
